import { useState } from 'react';
import { Alert, Button, Group, Select, Stack, Stepper, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { PageHeader } from '../../../components/PageHeader';
import { useOpenRun, useRecipe, useRun, useStages } from '../api';
import { nowForDatetimeLocal } from '../format';
import { pendingRecipeConsumables } from '../recipePreview';
import { ClosePostPanel } from './ClosePostPanel';
import { RunConsumablesEditor } from './RunConsumablesEditor';
import { RunInputsEditor } from './RunInputsEditor';
import { RunOutputsEditor } from './RunOutputsEditor';
import { YieldVarianceBadge } from './YieldVarianceBadge';

const STEP_LABELS = ['Stage & shift', 'Inputs', 'Outputs', 'Consumables', 'Review & post'];

/** "Record a shift" guided flow (BUILD_SPEC.md §8.3 SHIFT mode, §11): stage +
 * shift label/times -> inputs -> outputs -> consumables (backflushed from recipe,
 * editable) -> yield-vs-recipe + ledger-effect preview -> Post. Opening a run is a
 * real server-side action (child input/output/consumable rows need a runId), so
 * step 1 opens the run immediately (same two-phase pattern
 * features/silo-counts/components/NewReadingFlow.tsx uses: calculate/create now,
 * review/post later) — every later step edits that SAME run via the shared line
 * editors, which the kiln-batch run detail page (routes/production-packconv/runs/
 * $runId.tsx) reuses verbatim. */
export function RecordShiftWizard() {
  const canOperate = useCanOperate();
  const [active, setActive] = useState(0);
  const [runId, setRunId] = useState<string | null>(null);
  const [stageId, setStageId] = useState<string | null>(null);
  const [shiftLabel, setShiftLabel] = useState('');
  const [startedAt, setStartedAt] = useState(nowForDatetimeLocal());

  const stagesQuery = useStages();
  const shiftStages = (stagesQuery.data?.items ?? []).filter((s) => s.runMode === 'SHIFT' && s.isActive);
  const selectedStage = shiftStages.find((s) => s.id === stageId);

  const openRun = useOpenRun();
  const runQuery = useRun(runId ?? undefined);
  const run = runQuery.data;
  const recipeQuery = useRecipe(run?.recipeId);

  function handleStart() {
    if (!selectedStage) return;
    openRun.mutate(
      {
        siteId: selectedStage.siteId,
        stageId: selectedStage.id,
        shiftLabel: shiftLabel.trim() || null,
        startedAt: new Date(startedAt),
      },
      {
        onSuccess: (created) => {
          setRunId(created.id);
          setActive(1);
        },
      },
    );
  }

  function resetWizard() {
    setActive(0);
    setRunId(null);
    setStageId(null);
    setShiftLabel('');
    setStartedAt(nowForDatetimeLocal());
    openRun.reset();
  }

  const missingConsumables = run ? pendingRecipeConsumables(recipeQuery.data, run.outputs, run.consumables).length : 0;

  if (run?.status === 'POSTED') {
    return (
      <Stack gap="lg" maw={640}>
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Shift posted</Title>
            <Text c="dimmed">{run.number}</Text>
          </div>
          {run.yieldVariancePct !== null && <YieldVarianceBadge variancePct={run.yieldVariancePct} />}
        </Group>
        <Group>
          <Button onClick={resetWizard}>Record another shift</Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Record a shift"
        description="Record a production shift — what went in, what came out, and post it to stock."
      />
      <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
        {STEP_LABELS.map((label) => (
          <Stepper.Step key={label} label={label} />
        ))}
      </Stepper>

      {active === 0 &&
        (runId ? (
          <Alert color="green" title="Shift started">
            {selectedStage?.name ?? 'Stage'} — {run?.number ?? runId}
          </Alert>
        ) : (
          <Stack gap="sm" maw={480}>
            <Text c="dimmed" size="sm">
              A stage is a step of the plant (Dryer, Hammer Mill, PM1); its recipe is the expected in:out ratio and
              consumables.
            </Text>
            <Select
              label="Stage"
              placeholder="Choose a SHIFT stage"
              data={shiftStages.map((s) => ({ value: s.id, label: s.name }))}
              value={stageId}
              onChange={setStageId}
              searchable
              disabled={stagesQuery.isLoading}
            />
            {!stagesQuery.isLoading && shiftStages.length === 0 && (
              <Alert color="amber" icon={<IconAlertTriangle size={18} />}>
                No active SHIFT stages configured — set one up on the Stages &amp; recipes tab first.
              </Alert>
            )}
            <TextInput
              label="Shift label (optional)"
              placeholder="e.g. Day shift"
              value={shiftLabel}
              onChange={(e) => setShiftLabel(e.currentTarget.value)}
            />
            <TextInput
              label="Started at"
              type="datetime-local"
              value={startedAt}
              onChange={(e) => setStartedAt(e.currentTarget.value)}
            />
            {openRun.isError && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {openRun.error instanceof ApiRequestError ? openRun.error.message : 'Failed to start the shift'}
              </Alert>
            )}
            <Group>
              <Tooltip label="Requires Operations role" disabled={canOperate}>
                <span>
                  <Button disabled={!canOperate || !selectedStage} loading={openRun.isPending} onClick={handleStart}>
                    Start shift
                  </Button>
                </span>
              </Tooltip>
            </Group>
          </Stack>
        ))}

      {active > 0 && run && (
        <>
          {active === 1 && (
            <RunInputsEditor runId={run.id} siteId={run.siteId} lines={run.inputs} editable={run.status === 'OPEN'} />
          )}
          {active === 2 && (
            <RunOutputsEditor runId={run.id} siteId={run.siteId} lines={run.outputs} editable={run.status === 'OPEN'} />
          )}
          {active === 3 && (
            <RunConsumablesEditor
              runId={run.id}
              siteId={run.siteId}
              lines={run.consumables}
              outputs={run.outputs}
              recipe={recipeQuery.data}
              editable={run.status === 'OPEN'}
            />
          )}
          {active === 4 && <ClosePostPanel run={run} recipe={recipeQuery.data} missingConsumableCount={missingConsumables} />}

          <Group justify="space-between">
            <Button variant="default" disabled={active <= 1} onClick={() => setActive((a) => Math.max(1, a - 1))}>
              Back
            </Button>
            {active < 4 && (
              <Button
                disabled={(active === 1 && run.inputs.length === 0) || (active === 2 && run.outputs.length === 0)}
                onClick={() => setActive((a) => Math.min(4, a + 1))}
              >
                Next
              </Button>
            )}
          </Group>
        </>
      )}
    </Stack>
  );
}
